import React, { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useEffectOnce } from 'react-use';
import { Card, Box } from '../design-system';
import { BarChart, Bar, XAxis, Cell } from 'recharts';
import { useData } from '../helpers/data-loader';
import { errorMsg } from '../helpers/utils';

interface YearToDateDashboardProps {
  height?: any;
}

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0, // this will remove the cents
  maximumFractionDigits: 0, // this will remove the cents
});

const statusList = [
  {
    name: 'Awarded',
    color: '#cccc34',
  },
  {
    name: 'Completed',
    color: '#67ab31',
  },
  {
    name: 'Declined',
    color: '#fe3437',
  },
  {
    name: 'Deferred',
    color: '#5fe1e5',
  },
  {
    name: 'Never Seek',
    color: '#c5c5c5',
  },
  {
    name: 'Pending',
    color: '#fe9256',
  },
  {
    name: 'Go/No-Go',
    color: '#ef6b6b',
  },
  {
    name: 'Working',
    color: '#ffde68',
  },
  {
    name: 'Scheduled',
    color: '#eebc89',
  },
];

const YearToDateDashboard = ({ height }: YearToDateDashboardProps) => {
  const navigate = useNavigate();
  const [ytdDataRequest, fetchYtdData] = useData('/project/ytdData', {
    errorMsg: errorMsg('the year-to-date dashboard'),
    initialData: {},
  });

  useEffectOnce(() => {
    fetchYtdData();
  });

  const { chart, totalProjects } = useMemo(() => {
    const projects = ytdDataRequest.projects || [];
    return {
      chart: statusList.flatMap((status) => {
        const statusProjects = projects.find(
          (item) => item.status === status.name,
        );
        return statusProjects
          ? [
              {
                name: status.name,
                // Awarded dollars actually received; requested dollars for
                // every other status (see ProjectController::yearToDate).
                amount: Number(statusProjects.amount),
                count: statusProjects.count,
                fill: status.color,
              },
            ]
          : [];
      }),
      totalProjects: projects.reduce(
        (total, project) => total + project.count,
        0,
      ),
    };
  }, [ytdDataRequest]);

  return (
    <>
      <Box m={0} p={2}>
        <Card style={{ minHeight: height }} p={2}>
          <Card.Body>
            <div className="w-100">
              <div>
                <p className="font-bold text-lg">Fiscal Year</p>
                <div className="flex flex-col w-100">
                  <div className="relative">
                    <BarChart
                      id="ytd-chart"
                      width={chart.length * 120}
                      height={298}
                      className="mt-2"
                      data={chart}
                      barGap={10}
                      barCategoryGap={10}
                    >
                      <Bar
                        dataKey="amount"
                        fill="#ddd"
                        onClick={(data) => {
                          if (data && data.name) {
                            navigate(
                              `/projects?deadline=365&status=${data.name}`,
                            );
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        {chart.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Bar>
                      <XAxis dataKey="name" />
                    </BarChart>
                    <div>
                      {chart.map((entry, index) => (
                        <a
                          id={`chart-${entry.name.replace(/[-/\s]/g, '')}`}
                          href={`/projects?deadline=365&status=${entry.name}`}
                          key={entry.name}
                          className="absolute bottom-0 left-0 mb-12 text-black flex flex-col items-center text-center flex-1"
                          style={{
                            marginLeft: 120 * index,
                            width: '120px',
                          }}
                        >
                          <p className="text-xl font-bold">{entry.count}</p>
                          <p className="text-xs opacity-80">
                            {formatter.format(entry.amount)}
                          </p>
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>
      </Box>

      <Box m={0} p={2}>
        {/* gap, not space-x: the Cards' styled-components m={0} margin
            overrides space-x's child margins */}
        <div className="flex gap-2">
          <Card m={0} p={2} flex={1}>
            <div className="px-3 flex justify-center items-center gap-2">
              <p className="text-md">Total Projects</p>
              <a
                id="total-projects"
                href="/projects?deadline=365"
                className="text-blue-600 underline"
              >
                {totalProjects > 0 && totalProjects}
              </a>
            </div>
          </Card>
          <Card m={0} p={2} flex={1}>
            <div className="px-3 flex justify-center items-center gap-2">
              <p className="text-md">Total Clients</p>
              <a
                id="total-clients"
                href="/clients"
                className="text-blue-600 underline"
              >
                {ytdDataRequest.clientCount}
              </a>
            </div>
          </Card>
        </div>
      </Box>
    </>
  );
};

export default YearToDateDashboard;
